// lib/tickets/ladeAuftragsDaten.ts
import { supabase } from '../supabase/client';
import { aktuelleZeile, type EinheitEigZeitraum } from '../eigentuemer/einheitEigentum';
import type { AuftragsDaten } from './auftragsMail';
import { eigentuemerAnzeigename } from '../eigentuemer/anzeige';
import { rechnungsEmpfaenger } from './rechnungsEmpfaenger';

export interface AuftragsKontext {
  daten: AuftragsDaten;
  anhaenge: { id: string; name: string; size: number; path: string }[];
  objektId: string | null;
  einheitId: string | null;
}

/** Parst "Vorname Nachname <email@example.com>" oder reine Adresse */
function parseVon(von: string): { name: string | null; email: string | null } {
  const m = von.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
  if (m) return { name: m[1].trim() || null, email: m[2].trim() };
  return { name: null, email: von.trim() || null };
}

const ROLLE: Record<string, string> = {
  mieter: 'Mieter',
  eigentuemer: 'Eigentümer',
  dienstleister: 'Dienstleister',
  hausmeister: 'Hausmeister',
};

export async function ladeAuftragsDaten(ticketId: string): Promise<AuftragsKontext> {
  const { data: ticket } = await supabase
    .from('tickets').select('id, beschreibung, objekt_id, mieter_id, kategorie_id, einheit_id, eigentuemer_id, dienstleister_vorschlag_id, dienstleister_id').eq('id', ticketId).single();
  if (!ticket) throw new Error('Ticket nicht gefunden');

  // verknüpfte Quell-Mail (entity_links: ticket -> email 'quelle')
  const { data: links } = await supabase
    .from('entity_links').select('target_id')
    .eq('source_type', 'ticket').eq('source_id', ticketId).eq('target_type', 'email').eq('link_type', 'quelle');
  const emailId = links?.[0]?.target_id ?? null;

  let analyse: any = null;
  let anhaenge: AuftragsKontext['anhaenge'] = [];
  let mailVon: string | null = null;
  if (emailId) {
    const { data: a } = await supabase
      .from('email_analysen')
      .select('zusammenfassung, dienstleister_vorschlag_id, dienstleister_id, einheit_id, objekt_id, mieter_id, eigentuemer_id, kategorie_id, absender_typ')
      .eq('email_id', emailId).maybeSingle();
    analyse = a;

    const { data: mail } = await supabase.from('emails').select('von').eq('id', emailId).maybeSingle();
    mailVon = mail?.von ?? null;

    const { data: anh } = await supabase
      .from('email_anhaenge').select('id, dateiname, dateigroesse, dateipfad').eq('email_id', emailId);
    anhaenge = (anh || []).map((x: any) => ({ id: x.id, name: x.dateiname, size: x.dateigroesse ?? 0, path: x.dateipfad }));
  }

  // Fallback-Ketten: Ticket-Feld hat Vorrang, Analyse ist Fallback
  const objektId = (ticket?.objekt_id ?? analyse?.objekt_id) ?? null;
  const kategorieId = (ticket?.kategorie_id ?? analyse?.kategorie_id) ?? null;
  const mieterId = (ticket?.mieter_id ?? analyse?.mieter_id) ?? null;

  const objekt = objektId
    ? (await supabase.from('objekte').select('strasse, hausnummer, plz, stadt, objektnummer, bezeichnung, objekttyp').eq('id', objektId).maybeSingle()).data
    : null;

  const einheitId = (ticket?.einheit_id ?? analyse?.einheit_id) ?? null;
  const einheit = einheitId
    ? (await supabase.from('objekt_einheiten').select('bezeichnung').eq('id', einheitId).maybeSingle()).data?.bezeichnung ?? null
    : null;

  // Eigentümer / Rechnungsempfänger (#534): Ticket → Analyse → Einheit → WEG bzw. alle
  // Objekt-Eigentümer. Vorher brach die Objekt-Abfrage bei mehreren Eigentümern ab
  // (maybeSingle) und der am Ticket verknüpfte Eigentümer wurde nicht gelesen.
  let einheitEigId: string | null = null;
  if (einheitId) {
    const ee = (await supabase.from('einheit_eigentuemer').select('eigentuemer_id, mea, hausgeld_vorschuss, vertrag_ab, vertrag_bis').eq('einheit_id', einheitId)).data ?? [];
    einheitEigId = aktuelleZeile(ee as ({ eigentuemer_id: string } & EinheitEigZeitraum)[], new Date().toISOString().slice(0, 10))?.eigentuemer_id ?? null;
  }
  const objektEigIds = objektId
    ? ((await supabase.from('objekt_eigentuemer').select('eigentuemer_id').eq('objekt_id', objektId)).data ?? []).map((r) => r.eigentuemer_id as string)
    : [];
  const ticketEigId: string | null = (ticket as { eigentuemer_id?: string | null }).eigentuemer_id ?? null;
  const analyseEigId: string | null = analyse?.eigentuemer_id ?? null;
  const alleEigIds = [...new Set([ticketEigId, analyseEigId, einheitEigId, ...objektEigIds].filter((x): x is string => !!x))];
  const eigDaten = alleEigIds.length
    ? ((await supabase.from('eigentuemer').select('id, anrede, vorname, nachname, firma_name, email, telefon').in('id', alleEigIds)).data ?? [])
    : [];
  const eigNach = new Map(eigDaten.map((e) => [e.id as string, e]));
  const nameVon = (id: string | null) => {
    const e = id ? eigNach.get(id) : null;
    const name = e ? eigentuemerAnzeigename(e) : null;
    return name && name !== '—' ? name : null;
  };
  const eigentuemer = rechnungsEmpfaenger({
    ticketEigentuemer: nameVon(ticketEigId),
    analyseEigentuemer: nameVon(analyseEigId),
    einheitEigentuemer: nameVon(einheitEigId),
    objekttyp: objekt?.objekttyp ?? null,
    objektBezeichnung: objekt?.bezeichnung ?? null,
    objektAdresse: [[objekt?.strasse, objekt?.hausnummer].filter(Boolean).join(' '), [objekt?.plz, objekt?.stadt].filter(Boolean).join(' ')].filter(Boolean).join(', '),
    objektEigentuemer: objektEigIds.map(nameVon).filter((n): n is string => !!n),
  });
  // Kontakt für „Meldender = Eigentümer“: nur bei einer eindeutigen Person.
  const eigKontaktId = ticketEigId ?? analyseEigId ?? einheitEigId ?? (objektEigIds.length === 1 ? objektEigIds[0] : null);
  const eigK = eigKontaktId ? eigNach.get(eigKontaktId) : null;
  const eigKontakt: { email: string | null; telefon: string | null } | null = eigK ? { email: eigK.email ?? null, telefon: eigK.telefon ?? null } : null;

  const kategorie = kategorieId
    ? (await supabase.from('ticket_kategorien').select('name').eq('id', kategorieId).maybeSingle()).data?.name ?? null
    : null;

  const mieter = mieterId
    ? (await supabase.from('mieter').select('vorname, nachname, telefon, mobil, email').eq('id', mieterId).maybeSingle()).data
    : null;

  // Dienstleister: KI-Snapshot am Ticket hat Vorrang (speist auch den KI-Assistenten),
  // dann Analyse-Vorschlag, dann direktes Analyse-Feld
  // bereits beauftragter Dienstleister (tickets.dienstleister_id) vor dem Vorschlag
  const dlId = ticket?.dienstleister_id ?? ticket?.dienstleister_vorschlag_id ?? analyse?.dienstleister_vorschlag_id ?? analyse?.dienstleister_id ?? null;
  const dienstleister = dlId
    ? (await supabase.from('dienstleister').select('name, email, telefon').eq('id', dlId).maybeSingle()).data
    : null;

  // Meldender: Mieter aus DB → sonst Eigentümer (wohnt z. B. selbst) → sonst Mail-Absender (nur extern)
  let meldender: AuftragsDaten['meldender'] = mieter ? {
    name: [mieter.vorname, mieter.nachname].filter(Boolean).join(' ') || null,
    rolle: 'Mieter',
    telefon: mieter.telefon || mieter.mobil || null,
    email: mieter.email || null,
  } : null;

  // Kein Mieter hinterlegt (z. B. Eigentümer bewohnt die Einheit selbst): Eigentümer als
  // Meldenden vorbelegen — Rolle "Eigentümer", Kontaktdaten aus dem Eigentümer-Datensatz.
  // Im Auftrag-vorbereiten-Dialog bleibt alles frei änderbar.
  // Nur eine eindeutige Person – nicht „WEG …“ oder „A und B“ (#534).
  const eigKontaktName = nameVon(eigKontaktId);
  if (!meldender && eigKontaktName) {
    meldender = {
      name: eigKontaktName,
      rolle: 'Eigentümer',
      telefon: eigKontakt?.telefon ?? null,
      email: eigKontakt?.email ?? null,
    };
  }

  if (!meldender && mailVon && !/immo-?schwede\.de/i.test(mailVon)) {
    const p = parseVon(mailVon);
    meldender = {
      name: p.name,
      rolle: ROLLE[analyse?.absender_typ ?? ''] || '',
      telefon: null,
      email: p.email,
    };
  }

  const daten: AuftragsDaten = {
    kategorie,
    objekt: objekt ? { strasse: objekt.strasse, hausnummer: objekt.hausnummer, plz: objekt.plz, ort: objekt.stadt ?? null, objektnummer: objekt.objektnummer, bezeichnung: objekt.bezeichnung } : null,
    einheit,
    eigentuemer,
    mangelBeschreibung: (analyse?.zusammenfassung || ticket?.beschreibung) ?? null,
    meldender,
    dienstleister: dienstleister ? { id: dlId, name: dienstleister.name, email: dienstleister.email, telefon: dienstleister.telefon } : null,
  };

  return { daten, anhaenge, objektId, einheitId };
}
